interface DatadogRumLike {
  addAction: (actionName: string, context?: Record<string, unknown>) => void;
  onReady?: (callback: () => void) => void;
  setGlobalContextProperty?: (key: string, value: unknown) => void;
}

interface DatadogQueuedAction {
  actionName: string;
  context?: Record<string, unknown>;
}

const datadogActionQueue: DatadogQueuedAction[] = [];
const DATADOG_QUEUE_TTL_MS = 30_000;
let hasPendingOnReadyFlush = false;
let firstQueuedAtMs: number | null = null;

declare global {
  interface Window {
    DD_RUM?: DatadogRumLike;
  }
}

function enqueueDatadogAction(action: DatadogQueuedAction): void {
  if (datadogActionQueue.length === 0) {
    firstQueuedAtMs = Date.now();
  }

  datadogActionQueue.push(action);
}

function dropStaleDatadogQueue(): void {
  if (
    firstQueuedAtMs !== null &&
    Date.now() - firstQueuedAtMs > DATADOG_QUEUE_TTL_MS
  ) {
    datadogActionQueue.length = 0;
    firstQueuedAtMs = null;
    hasPendingOnReadyFlush = false;
  }
}

function flushDatadogActionQueue(): void {
  const rumClient = window.DD_RUM;
  if (!rumClient) {
    return;
  }

  while (datadogActionQueue.length > 0) {
    const queuedAction = datadogActionQueue.shift();

    if (!queuedAction) {
      continue;
    }

    try {
      rumClient.addAction(queuedAction.actionName, queuedAction.context);
    } catch (error) {
      datadogActionQueue.unshift(queuedAction);
      return;
    }
  }

  firstQueuedAtMs = null;
}

export function emitDatadogAction(
  actionName: string,
  context?: Record<string, unknown>
): void {
  dropStaleDatadogQueue();
  enqueueDatadogAction({ actionName, context });

  const rumClient = window.DD_RUM;

  if (!rumClient) {
    return;
  }

  if (typeof rumClient.onReady === "function") {
    if (hasPendingOnReadyFlush) {
      return;
    }

    hasPendingOnReadyFlush = true;
    try {
      rumClient.onReady(() => {
        hasPendingOnReadyFlush = false;
        flushDatadogActionQueue();
      });
    } catch (error) {
      hasPendingOnReadyFlush = false;
    }
    return;
  }

  flushDatadogActionQueue();
}
